import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { formatCurrency } from '../utils/format';
import {
  getTargets,
  generateTargets,
  copyTargetsFromPrevious,
  overrideTarget,
  resetTargetOverride,
} from '../api/targets';

const METHODS = {
  average: {
    label: '3-Month Weighted Average',
    text: 'Normal performance based on the last 3 complete months: SUM(GP) / SUM(Revenue) across those months. Not an average of monthly percentages.',
  },
  best: {
    label: 'Best Qualifying Month',
    text: 'Highest proven GP% achieved in the last 3 complete months, excluding unusually low-volume months (under 30% of the period’s average monthly revenue). A stretch planning method.',
  },
};

const pct = (v, dp = 2) => (v === null || v === undefined ? '—' : `${Number(v).toFixed(dp)}%`);
const pts = (v) => (v === null || v === undefined ? '—' : `${v >= 0 ? '+' : ''}${Number(v).toFixed(2)}`);

function defaultMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

// Click-to-edit number cell. Saves on Enter/blur, Escape cancels.
function EditableNumber({ value, format, disabled, onSave, step = '0.01' }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');

  if (disabled || value === null || value === undefined) return <span>{format(value)}</span>;
  if (!editing) {
    return (
      <button
        type="button"
        title="Click to override"
        onClick={() => { setDraft(String(value)); setEditing(true); }}
        className="underline decoration-dotted underline-offset-2 hover:text-blue-700"
      >
        {format(value)}
      </button>
    );
  }
  const commit = () => {
    setEditing(false);
    const n = Number(draft);
    if (draft !== '' && Number.isFinite(n) && n !== value) onSave(n);
  };
  return (
    <input
      autoFocus
      type="number"
      step={step}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') commit();
        if (e.key === 'Escape') setEditing(false);
      }}
      className="w-24 border border-blue-400 rounded px-1 py-0.5 text-right text-sm"
    />
  );
}

function ScopeTable({ title, nameLabel, rows, reconciliation, canEdit, amountFirst, onOverride, onReset }) {
  if (!rows.length) return null;
  return (
    <section className="bg-white border border-gray-200 rounded-lg mb-6">
      <h2 className="px-4 py-3 text-sm font-semibold text-gray-800 border-b border-gray-200">{title}</h2>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-xs text-gray-600">
            <tr>
              <th className="px-3 py-2 text-left">{nameLabel}</th>
              <th className="px-3 py-2 text-right">3M Avg GP%</th>
              <th className="px-3 py-2 text-right">Best Qualifying</th>
              <th className="px-3 py-2 text-right">Baseline</th>
              <th className="px-3 py-2 text-right">Uplift</th>
              <th className="px-3 py-2 text-right">Suggested</th>
              {amountFirst && <th className="px-3 py-2 text-right">Target GP (QAR)</th>}
              <th className="px-3 py-2 text-right">Target GP%</th>
              {!amountFirst && <th className="px-3 py-2 text-right">Target GP (QAR)</th>}
              <th className="px-3 py-2 text-left">Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const amountCell = (
                <td className="px-3 py-2 text-right tabular-nums">
                  <EditableNumber
                    value={r.insufficientData ? null : r.targetGpAmount}
                    format={(v) => (v == null ? '—' : formatCurrency(v))}
                    disabled={!canEdit}
                    step="1"
                    onSave={(n) => onOverride(r._id, { targetGpAmount: n })}
                  />
                </td>
              );
              return (
                <tr key={r._id} className="border-t border-gray-100">
                  <td className="px-3 py-2">{r.name || r.key}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{pct(r.averageGpPercent)}</td>
                  <td className="px-3 py-2 text-right tabular-nums" title={r.bestMonth ? `Best month: ${r.bestMonth}` : ''}>{pct(r.bestGpPercent)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{pct(r.baselineGpPercent)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{r.insufficientData ? '—' : pts(r.upliftPercent)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{pct(r.suggestedGpPercent ?? r.targetGpPercent)}</td>
                  {amountFirst && amountCell}
                  <td className="px-3 py-2 text-right tabular-nums font-medium">
                    <EditableNumber
                      value={r.targetGpPercent}
                      format={pct}
                      disabled={!canEdit}
                      onSave={(n) => onOverride(r._id, { targetGpPercent: n })}
                    />
                  </td>
                  {!amountFirst && amountCell}
                  <td className="px-3 py-2">
                    {r.insufficientData ? (
                      <span className="text-xs text-amber-700" title="Enter this target manually once history exists">
                        {r.insufficientReason || 'Insufficient historical data'}
                      </span>
                    ) : r.isManualOverride ? (
                      <span className="inline-flex items-center gap-2">
                        <span className="text-xs px-2 py-0.5 rounded bg-amber-100 text-amber-800">MANUAL OVERRIDE</span>
                        {canEdit && (
                          <button type="button" onClick={() => onReset(r._id)} className="text-xs text-blue-600 hover:underline">
                            Reset
                          </button>
                        )}
                      </span>
                    ) : (
                      <span className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-600">AUTO</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {reconciliation && (
        <div className="px-4 py-2 text-xs text-gray-600 border-t border-gray-100">
          Allocated target {pct(reconciliation.allocatedPercent)} vs company target {pct(reconciliation.companyTargetPercent)} ·{' '}
          <span className="font-medium">Unallocated target gap: {pts(reconciliation.unallocatedGapPercent)} pts</span>{' '}
          <span className="text-gray-400">(informational — targets are never adjusted to close it)</span>
        </div>
      )}
    </section>
  );
}

export default function GpTargets() {
  const { isAdmin, canViewGP } = useAuth();
  const [month, setMonth] = useState(defaultMonth());
  const [data, setData] = useState(null);
  const [method, setMethod] = useState('average');
  const [companyTarget, setCompanyTarget] = useState('');
  const [revenueTarget, setRevenueTarget] = useState('');
  const [resetOverrides, setResetOverrides] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const apply = useCallback((d) => {
    setData(d);
    setMethod(d.method || 'average');
    if (d.overall) {
      setCompanyTarget(String(d.overall.targetGpPercent ?? ''));
      setRevenueTarget(d.overall.revenueIsManual ? String(d.overall.targetRevenue) : '');
    } else {
      setCompanyTarget('');
      setRevenueTarget('');
    }
  }, []);

  useEffect(() => {
    if (!canViewGP) return;
    setLoading(true);
    setError('');
    setNotice('');
    getTargets(month)
      .then(apply)
      .catch((e) => setError(e.response?.data?.message || 'Could not load targets'))
      .finally(() => setLoading(false));
  }, [month, canViewGP, apply]);

  async function run(fn, okMessage) {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      apply(await fn());
      if (okMessage) setNotice(okMessage);
    } catch (e) {
      setError(e.response?.data?.message || 'Request failed');
    } finally {
      setBusy(false);
    }
  }

  const generate = () =>
    run(
      () => generateTargets({ month, method, companyTargetPercent: companyTarget, targetRevenue: revenueTarget, resetOverrides }),
      resetOverrides ? 'Targets regenerated; manual overrides were reset.' : 'Targets calculated. Manual overrides were kept.'
    ).then(() => setResetOverrides(false));

  const refreshAfter = (fn) => async (...args) => {
    setError('');
    try {
      await fn(...args);
      apply(await getTargets(month));
    } catch (e) {
      setError(e.response?.data?.message || 'Could not save');
    }
  };
  const onOverride = refreshAfter((id, body) => overrideTarget(id, body));
  const onReset = refreshAfter((id) => resetTargetOverride(id));

  if (!canViewGP) {
    return (
      <div>
        <h1 className="text-xl font-semibold text-gray-800 mb-2">GP Targets</h1>
        <p className="text-sm text-gray-600">Target and GP data needs the approve action on this module.</p>
      </div>
    );
  }
  const overall = data?.overall;
  const canEdit = isAdmin;

  return (
    <div>
      <h1 className="text-xl font-semibold text-gray-800 mb-1">GP Targets</h1>
      <p className="text-sm text-gray-600 mb-4">
        Enter one company GP target. Each channel, category and salesman gets its own baseline plus the same required uplift — nobody is given the flat company %.
      </p>

      {error && <div className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</div>}
      {notice && <div className="mb-4 text-sm text-green-800 bg-green-50 border border-green-200 rounded px-3 py-2">{notice}</div>}

      <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6">
        <div className="flex flex-wrap gap-4 items-end">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Month</label>
            <input type="month" value={month} onChange={(e) => e.target.value && setMonth(e.target.value)} className="border border-gray-300 rounded px-3 py-1.5 text-sm" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Baseline method</label>
            <select value={method} onChange={(e) => setMethod(e.target.value)} disabled={!canEdit} className="border border-gray-300 rounded px-3 py-1.5 text-sm">
              {Object.entries(METHODS).map(([k, m]) => (
                <option key={k} value={k}>{m.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Company GP target %</label>
            <input type="number" step="0.01" min="0" max="100" value={companyTarget} onChange={(e) => setCompanyTarget(e.target.value)} disabled={!canEdit} className="w-28 border border-gray-300 rounded px-3 py-1.5 text-sm" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Revenue target (optional)</label>
            <input type="number" step="1" min="0" value={revenueTarget} onChange={(e) => setRevenueTarget(e.target.value)} disabled={!canEdit} placeholder="3-month avg" className="w-36 border border-gray-300 rounded px-3 py-1.5 text-sm" />
          </div>
          {canEdit && (
            <>
              <button type="button" onClick={generate} disabled={busy || companyTarget === ''} className="px-3 py-2 text-sm rounded bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white">
                {busy ? 'Working…' : overall ? 'Recalculate' : 'Generate'}
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => run(() => copyTargetsFromPrevious(month), 'Copied from last month. Baselines were recalculated for this month.')}
                className="px-3 py-2 text-sm rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
              >
                Copy from Last Month
              </button>
              <label className="flex items-center gap-2 text-xs text-gray-600">
                <input type="checkbox" checked={resetOverrides} onChange={(e) => setResetOverrides(e.target.checked)} />
                Also reset manual overrides
              </label>
            </>
          )}
        </div>
        <p className="mt-3 text-xs text-gray-600">
          <span className="font-medium">{METHODS[method].label}:</span> {METHODS[method].text}
        </p>
        {data && data.method !== method && (
          <p className="mt-1 text-xs text-amber-700">Method changed — press {overall ? 'Recalculate' : 'Generate'} to apply it. Saved targets still use {METHODS[data.method]?.label}.</p>
        )}
      </div>

      {loading && <p className="text-sm text-gray-500">Loading…</p>}

      {!loading && !overall && (
        <p className="text-sm text-gray-600">No targets saved for this month yet. Enter a company target and press Generate.</p>
      )}

      {overall && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <div className="bg-white border border-gray-200 rounded-lg p-3">
              <div className="text-xs text-gray-500">Company baseline ({METHODS[overall.baselineMethod]?.label})</div>
              <div className="text-lg font-semibold text-gray-800">{overall.insufficientData ? '—' : pct(overall.baselineGpPercent)}</div>
              <div className="text-xs text-gray-500">3M avg {pct(overall.averageGpPercent)} · best {pct(overall.bestGpPercent)}</div>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-3">
              <div className="text-xs text-gray-500">Company target</div>
              <div className="text-lg font-semibold text-gray-800">{pct(overall.targetGpPercent)}</div>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-3">
              <div className="text-xs text-gray-500">Required uplift</div>
              <div className="text-lg font-semibold text-gray-800">{pts(overall.upliftPercent)} pts</div>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-3">
              <div className="text-xs text-gray-500">Target GP (QAR)</div>
              <div className="text-lg font-semibold text-gray-800">{formatCurrency(overall.targetGpAmount)}</div>
              <div className="text-xs text-gray-500">on revenue {formatCurrency(overall.targetRevenue)}{overall.revenueIsManual ? '' : ' (3M avg)'}</div>
            </div>
          </div>

          <ScopeTable title="Category targets" nameLabel="Category" rows={data.category} reconciliation={data.reconciliation?.category} canEdit={canEdit} onOverride={onOverride} onReset={onReset} />
          <ScopeTable title="Channel targets" nameLabel="Channel" rows={data.channel} reconciliation={data.reconciliation?.channel} canEdit={canEdit} onOverride={onOverride} onReset={onReset} />
          <ScopeTable title="Salesman targets — GP amount is the primary target" nameLabel="Salesman" rows={data.salesman} reconciliation={data.reconciliation?.salesman} canEdit={canEdit} amountFirst onOverride={onOverride} onReset={onReset} />
        </>
      )}
    </div>
  );
}
